import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareBar from "@/pages/news/components/ShareBar";
import { getNewsById, getRelatedNews } from "@/mocks/news";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const item = getNewsById(id);

  usePageMeta({
    title: item
      ? `${item.title} - 张津瑜个人品牌官网`
      : "资讯未找到 - 张津瑜个人品牌官网",
    description: item ? item.summary.slice(0, 150) : "抱歉，您查看的资讯不存在。",
    keywords: item ? ["张津瑜", item.category, ...item.tags].join(",") : "张津瑜资讯",
  });

  if (!item) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 py-20 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          抱歉，没有找到这篇资讯
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          该资讯可能已被删除或链接有误，欢迎返回资讯中心浏览其他内容。
        </p>
        <Link
          to="/news"
          className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回资讯中心
        </Link>
      </div>
    );
  }

  const related = getRelatedNews(item, 3);

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 py-6">
        <div className="mx-auto max-w-4xl px-4 md:px-6 lg:px-10">
          <Breadcrumb
            items={[
              { label: "资讯中心", to: "/news" },
              { label: item.category, to: `/news?category=${encodeURIComponent(item.category)}` },
              { label: "正文" },
            ]}
          />
        </div>
      </section>

      <article className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-4xl px-4 md:px-6 lg:px-10">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary-50 px-3 py-1 font-label text-xs font-medium text-primary-700">
              {item.category}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-calendar-line" />
              {item.date}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-user-line" />
              {item.author}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-eye-line" />
              {item.views.toLocaleString()}
            </span>
          </div>

          <h1 className="mt-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {item.title}
          </h1>

          <div className="mt-6 flex items-center justify-between gap-4 border-y border-background-200 py-4">
            <p className="line-clamp-2 flex-1 text-sm text-foreground-500">{item.summary}</p>
            <ShareBar title={item.title} />
          </div>

          <div className="mt-8 h-64 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src={item.cover}
              alt={`${item.title} - 张津瑜资讯配图`}
              title={`${item.title} + 张津瑜`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-10 space-y-6">
            {item.content.map((paragraph, index) => (
              <p
                key={index}
                className="text-base leading-[1.9] text-foreground-800 md:text-[17px]"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
            <span className="text-sm text-foreground-500">标签：</span>
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="mb-8 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关资讯推荐
            </h2>
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-accent-600"
            >
              更多资讯
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((rel) => (
              <Link
                key={rel.id}
                to={`/news/detail/${rel.id}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="h-44 w-full overflow-hidden">
                  <img
                    src={rel.cover}
                    alt={`${rel.title} - 张津瑜相关资讯`}
                    title={`${rel.title} + 张津瑜资讯`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="font-label text-xs font-semibold uppercase tracking-wider text-accent-600">
                    {rel.category}
                  </span>
                  <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                    {rel.title}
                  </h3>
                  <div className="mt-auto flex items-center gap-1.5 pt-4 text-xs text-foreground-500">
                    <i className="ri-calendar-line" />
                    {rel.date}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}